import { confirmPiSDKHost } from './piDetection'

export interface PiUser {
  uid: string
  username: string
  avatarUrl?: string
  kycStatus?: string
  walletAddress?: string
}

/**
 * Every SDK sign-in must request all three scopes. Pi keeps the scopes of the
 * user's latest authenticate, so asking for fewer (say, at checkout) silently
 * revokes `wallet_address`, and A2U payouts to that user then fail with
 * `missing_scope`.
 */
export const PI_SDK_SCOPES = ['username', 'payments', 'wallet_address']

export interface PiAuthResult {
  accessToken: string
  refreshToken?: string
  user: PiUser
}

export interface PiPaymentRequest {
  amount: number
  memo: string
  metadata: Record<string, string>
}

/** Callbacks mirroring the Pi SDK's U2A payment flow. */
export interface PiPaymentCallbacks {
  /** Fires as soon as Pi Servers accept the payment; the server must approve it. */
  onReadyForServerApproval(paymentId: string): void | Promise<void>
  /** Fires after the user submits the blockchain transaction. */
  onReadyForServerCompletion(paymentId: string, txid: string): void | Promise<void>
  onCancel?(paymentId: string): void
  onError?(error: Error, payment?: unknown): void
}

/**
 * Seam between TalentSwap and whatever Pi runtime is present:
 * the in-browser Pi SDK, an external browser (no SDK), or a test mock.
 */
export interface PiAdapter {
  readonly name: string
  /** Prepares the runtime; returns an existing session if one is available. */
  init(appId: string, sandbox?: boolean): Promise<PiAuthResult | null>
  authenticate(scopes?: string[], onIncomplete?: (payment: PiIncompletePayment) => void): Promise<PiAuthResult>
  createPayment(payment: PiPaymentRequest, callbacks: PiPaymentCallbacks): void
}

// --- In-app adapter: wraps the Pi Browser SDK ---

let piScriptPromise: Promise<void> | null = null

export function loadPiSDK(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available'))
  }
  if (window.Pi) {
    return Promise.resolve()
  }
  if (piScriptPromise) {
    return piScriptPromise
  }

  // Loading the script is also allowed for an explicit host probe in an
  // unidentified webview. The presence of window.Pi does not prove a Pi host.
  piScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="pi-sdk.js"]') as HTMLScriptElement | null
    if (existingScript) {
      if (window.Pi) {
        resolve()
        return
      }
      const checkInterval = setInterval(() => {
        if (window.Pi) {
          clearInterval(checkInterval)
          clearTimeout(timeoutId)
          resolve()
        }
      }, 50)
      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval)
        piScriptPromise = null
        reject(new Error('Timed out waiting for Pi SDK'))
      }, 10000)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://sdk.minepi.com/pi-sdk.js'
    script.async = true

    const timeoutId = setTimeout(() => {
      cleanup()
      piScriptPromise = null
      reject(new Error('Timed out loading Pi SDK script'))
    }, 10000)

    function cleanup() {
      clearTimeout(timeoutId)
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
      if (!window.Pi) script.remove()
    }

    function onLoad() {
      cleanup()
      if (window.Pi) {
        resolve()
      } else {
        piScriptPromise = null
        reject(new Error('Pi SDK script loaded but window.Pi not available'))
      }
    }

    function onError() {
      cleanup()
      piScriptPromise = null
      reject(new Error('Failed to load Pi SDK script'))
    }

    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })
    document.head.appendChild(script)
  })

  return piScriptPromise
}

function requirePi(): PiSDK {
  const pi = window.Pi
  if (!pi) throw new Error('Pi SDK not available')
  return pi
}

const PI_HOST_ERROR = 'Could not reach Pi Browser. In Pi Browser, open TalentSwap using the Developer Portal sandbox link and retry. In another app, open TalentSwap in Safari or Chrome to sign in.'

async function initializePiHost(sandbox: boolean): Promise<void> {
  const pi = requirePi()
  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      (async () => {
        await pi.init({ version: '2.0', sandbox })
        const info = await pi.getPiHostAppInfo()
        if (info?.hostApp !== 'pi-browser') throw new Error(PI_HOST_ERROR)
      })(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error(PI_HOST_ERROR)), 10000)
      }),
    ])
  } catch {
    throw new Error(PI_HOST_ERROR)
  } finally {
    clearTimeout(timeout)
  }
}

const incompletePayments = new Set<string>()
export function pendingPiPayments(): string[] { return [...incompletePayments] }
export function recoveredPiPayment(id: string): void { incompletePayments.delete(id) }
function onIncompletePaymentFound(payment: PiIncompletePayment) {
  incompletePayments.add(payment.identifier)
}

export class InAppPiAdapter implements PiAdapter {
  readonly name = 'in-app'
  private initialized = false
  private initPromise: Promise<void> | null = null
  private authInFlight = false

  async init(_appId: string, sandbox = true): Promise<PiAuthResult | null> {
    if (this.initialized) return null
    if (this.initPromise) {
      await this.initPromise
      return null
    }

    this.initPromise = (async () => {
      try {
        await loadPiSDK()
        await initializePiHost(sandbox)
        confirmPiSDKHost()
        this.initialized = true
      } catch (err) {
        this.initialized = false
        this.initPromise = null
        throw err
      }
    })()

    await this.initPromise
    return null
  }

  async authenticate(scopes: string[] = PI_SDK_SCOPES, onIncomplete?: (payment: PiIncompletePayment) => void): Promise<PiAuthResult> {
    if (!this.initialized) {
      await this.init('', import.meta.env.VITE_PI_SANDBOX === 'true')
    }

    if (this.authInFlight) {
      throw new Error('Pi authentication is already in progress')
    }
    this.authInFlight = true

    try {
      const auth = await requirePi().authenticate(scopes, (payment) => {
        onIncompletePaymentFound(payment)
        onIncomplete?.(payment)
      })
      return {
        accessToken: auth.accessToken,
        refreshToken: auth.refreshToken,
        user: {
          uid: auth.user.uid,
          username: auth.user.username,
          avatarUrl: auth.user.avatarUrl,
          kycStatus: auth.user.kycStatus,
          walletAddress: auth.user.walletAddress || auth.user.wallet_address,
        },
      }
    } finally {
      this.authInFlight = false
    }
  }

  resetForTesting(): void {
    this.initialized = false
    this.initPromise = null
    this.authInFlight = false
    piScriptPromise = null
  }

  createPayment(payment: PiPaymentRequest, callbacks: PiPaymentCallbacks): void {
    requirePi().createPayment(
      {
        amount: payment.amount,
        memo: payment.memo,
        metadata: payment.metadata,
      },
      {
        onReadyForServerApproval: (paymentId) => {
          void Promise.resolve().then(() => callbacks.onReadyForServerApproval(paymentId))
            .catch((error) => callbacks.onError?.(error))
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          void Promise.resolve().then(() => callbacks.onReadyForServerCompletion(paymentId, txid))
            .catch((error) => callbacks.onError?.(error))
        },
        onCancel: (paymentId) => callbacks.onCancel?.(paymentId),
        onError: (error, payment) => callbacks.onError?.(error, payment),
      },
    )
  }
}

// --- External-browser adapter: no SDK, sign-in happens via OAuth redirect ---

export class ExternalBrowserPiAdapter implements PiAdapter {
  readonly name = 'external'

  async init(): Promise<PiAuthResult | null> {
    return null
  }

  async authenticate(): Promise<PiAuthResult> {
    throw new Error('Pi payments and SDK auth require the Pi Browser. Use Pi Sign-in instead.')
  }

  createPayment(): void {
    throw new Error('Payments require the Pi Browser.')
  }
}

// --- Mock adapter: deterministic identity for e2e/tests ---

const MOCK_IDENTITY: PiAuthResult = {
  accessToken: 'mock-access-token',
  refreshToken: 'mock-refresh-token',
  user: {
    uid: 'mock-uid-alice',
    username: 'alice',
    avatarUrl: undefined,
    kycStatus: 'approved',
  },
}

export class MockPiAdapter implements PiAdapter {
  readonly name = 'mock'
  private result: PiAuthResult = MOCK_IDENTITY

  setIdentity(user: Partial<PiUser>) {
    this.result = { ...MOCK_IDENTITY, user: { ...MOCK_IDENTITY.user, ...user } }
  }

  setAuthResult(result: PiAuthResult) {
    this.result = result
  }

  async init(): Promise<PiAuthResult | null> {
    return null
  }

  async authenticate(): Promise<PiAuthResult> {
    return this.result
  }

  createPayment(payment: PiPaymentRequest, callbacks: PiPaymentCallbacks): void {
    const paymentId = `mock-payment-${payment.amount}-${Date.now()}`
    // Simulate the SDK sequence: server approval, then the on-chain tx.
    setTimeout(() => {
      Promise.resolve(callbacks.onReadyForServerApproval(paymentId))
        .then(() => {
          return callbacks.onReadyForServerCompletion(paymentId, `mock-txid-${paymentId}`)
        })
        .catch((error) => callbacks.onError?.(error instanceof Error ? error : new Error(String(error))))
    }, 300)
  }
}

// --- Resolution ---

export const inAppPiAdapter = new InAppPiAdapter()
export const externalBrowserPiAdapter = new ExternalBrowserPiAdapter()
export const mockPiAdapter = new MockPiAdapter()

export function shouldUseMockPi(): boolean {
  return (
    import.meta.env.VITE_PI_MOCK === '1' ||
    (typeof window !== 'undefined' && !!window.__PI_MOCK__)
  )
}

export function resolvePiAdapter(isInsidePiBrowser: () => boolean): PiAdapter {
  if (shouldUseMockPi()) return mockPiAdapter
  if (isInsidePiBrowser()) return inAppPiAdapter
  return externalBrowserPiAdapter
}
